import React, { ReactNode } from 'react';
import { markInsurerPayoutAsPaid, markPayoutAsPaid } from '../../api/requests';
import Button from '../../components/form-control/Button';
import { Payout } from '../../models/payout/Payout';
import { PAYOUT_REVIEW_STATES, COMPANY_TYPES } from '../../resources/enums';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/form-control/Input';
import TextArea from '../../components/form-control/TextArea';
import { IRequestResponse } from '../../resources/interfaces';
import DatePickerInput from '../../components/form-control/DatePickerInput';
import { showToast } from '../../utils/miscFunctions';

interface IPayoutReviewProps {
  payout: Payout;
  onProceed: () => void;
  onCancel: () => void;
  type: PAYOUT_REVIEW_STATES;
  title: string;
  titleIcon: ReactNode | JSX.Element;
}
export interface IPayoutTransactionDetailsDTO {
  referenceNumber: string;
  payeeBankName: string;
  payeeAccountNumber: string;
  dateOfPayment: string;
  paymentApprover: string;
  remarks: string;
}

const MarkAsPaidModal = (props: IPayoutReviewProps) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const handleSubmit = (values: any) => {
    const payload: IPayoutTransactionDetailsDTO = {
      ...values
    };

    setSubmitting(true);
    if (props.type === PAYOUT_REVIEW_STATES.PAID && props.payout.broker?.type === COMPANY_TYPES.AGENCY) {
      markPayoutAsPaid(props.payout.id, props.payout.broker?.id ?? '', payload)
        .then((response: any) => {
          showToast.success(response.description);
          props.onProceed();
        })
        .catch((error: IRequestResponse) => {
          showToast.error(error.description);
        })
        .finally(() => setSubmitting(false));     
    }
    if (
      props.type === PAYOUT_REVIEW_STATES.PAID &&
      props.payout.broker?.type === COMPANY_TYPES.INSURER
    ) {
      markInsurerPayoutAsPaid(props.payout.id, payload)
        .then((response: IRequestResponse) => {
          showToast.success(response.message);
          props.onProceed();
        })
        .catch((error: IRequestResponse) => {
          showToast.error(error.description);
        })
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <div className="bg-white rounded-xl">

      <div className="p-[1rem] w-full">
        <Formik
          initialValues={{
            referenceNumber: '',
            payeeBankName: '',
            payeeAccountNumber: '',
            dateOfPayment: '',
            paymentApprover: '',
            remarks: ''
          }}
          validateOnBlur
          validationSchema={Yup.object({
            referenceNumber: Yup.string().required('Payout reference number is required.'),
            payeeBankName: Yup.string().required('Bank of payee is required.'),
            payeeAccountNumber: Yup.string().required('Account of payee is requied.'),
            paymentApprover: Yup.string().required('Name of Approver is required')
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}>
          {({ values, errors, handleSubmit, handleChange, handleBlur, touched, setFieldValue }) => (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-8">
                <Input
                  type="text"
                  name="referenceNumber"
                  label="Reference number"
                  value={values.referenceNumber}
                  placeholder="Eg: c10fabca-60ce-456e-..."
                  wrapperClasses=""
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.referenceNumber ? errors.referenceNumber : undefined}
                  helperText={touched?.referenceNumber ? errors.referenceNumber : undefined}
                />

                <Input
                  name="payeeBankName"
                  label="Bank name of payee"
                  value={values.payeeBankName}
                  placeholder="Eg: Ecobank"
                  type="text"
                  wrapperClasses=""
                  required
                  onChange={handleChange}
                  onBlur={handleChange}
                  error={touched?.payeeBankName ? errors.payeeBankName : undefined}
                  helperText={touched?.payeeBankName ? errors.payeeBankName : undefined}
                />

                <Input
                  name="payeeAccountNumber"
                  label=" Bank account of payee"
                  value={values.payeeAccountNumber}
                  type="text"
                  placeholder="Eg: "
                  wrapperClasses=""
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.payeeAccountNumber ? errors.payeeAccountNumber : undefined}
                  helperText={touched?.payeeAccountNumber ? errors.payeeAccountNumber : undefined}
                />
                <DatePickerInput
                  label="Date of Payment"
                  id="dateOfPayment"
                  placeholder="Select Date of Payment"
                  selected={values.dateOfPayment ? new Date(values.dateOfPayment) : null}
                  onChange={(date) => setFieldValue('dateOfPayment', date)}
                  allowFutureSelections={false}
                  error={touched.dateOfPayment ? String(errors.dateOfPayment) : undefined}
                  helperText={touched.dateOfPayment ? String(errors.dateOfPayment) : undefined}
                  allowEarlierSelections={false}
                  required
                />
              </div>
              <div className="">
                <Input
                  name="paymentApprover"
                  label="Payment Approver"
                  value={values.paymentApprover}
                  type="text"
                  placeholder="Eg: John Snow"
                  wrapperClasses=""
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.paymentApprover ? errors.paymentApprover : undefined}
                  helperText={touched?.paymentApprover ? errors.paymentApprover : undefined}
                  className=""
                />
              </div>

              <div className="">
                <TextArea
                  name={'remarks'}
                  value={values.remarks}
                  label="Remarks"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Give remarks..."
                  error={touched?.remarks ? errors.remarks : undefined}
                  helperText={touched?.remarks ? errors.remarks : undefined}
                />
              </div>

              <div className="w-full flex gap-4 ">
                <Button
                  label="Cancel"
                  bgColor='secondary'
                  fullWidth
                  onClick={props.onCancel}
                />
                <Button
                  label="Confirm"
                  onClick={handleSubmit}
                  bgColor='primary'
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default MarkAsPaidModal;
